import React, { useEffect, useState } from "react";
import {
  Alert,
  AutoComplete,
  Button,
  Input,
  List,
  Space,
  Typography,
} from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { GPS, geocodeCandidate } from "../../../types";
import { useDebounce } from "../../../hooks/useDebounce";
import { useLazyGeocodeSuggestQuery } from "../../../actions/trapsiteInQuery";
import {
  DRIFT_THRESHOLD_M,
  formatDistance,
  haversineMeters,
} from "../../../utils/geo";
import {
  POPUP_Z_INDEX,
  SUGGEST_DEBOUNCE_MS,
  SUGGEST_MIN_CHARS,
} from "./constants";

const { Text } = Typography;

interface Props {
  address: string;
  pin: GPS;
  geocodedGps: GPS | null;
  candidates: geocodeCandidate[];
  searching: boolean;
  reverseLoading: boolean;
  /** Set when a lookup failed, so the panel can say so in place. */
  lookupError: string | null;
  /** True once a lookup has run and matched nothing. */
  noMatch: boolean;
  onAddressChange: (address: string) => void;
  onFind: (address?: string) => void;
  onUpdateFromPin: () => void;
  onPickCandidate: (candidate: geocodeCandidate) => void;
  onSnapPinToAddress: () => void;
  container: HTMLElement | null;
}

/**
 * The address input, its lookup affordances, and the drift warning.
 *
 * Typing offers suggestions, which are cheap and non-storable. Choosing one -
 * or pressing Find - runs the real geocode, which is the billable, storable
 * call and the only thing that sets coordinates. Nothing geocodes on blur:
 * blur fires on every Tab, on the device picker and on Save.
 */
const AddressField: React.FC<Props> = ({
  address,
  pin,
  geocodedGps,
  candidates,
  searching,
  reverseLoading,
  lookupError,
  noMatch,
  onAddressChange,
  onFind,
  onUpdateFromPin,
  onPickCandidate,
  onSnapPinToAddress,
  container,
}) => {
  const [driftAcknowledged, setDriftAcknowledged] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [runSuggest] = useLazyGeocodeSuggestQuery();

  const debouncedAddress = useDebounce(address, SUGGEST_DEBOUNCE_MS);

  // A new lookup is a new decision: having dismissed the warning for one
  // address must not silently suppress it for the next.
  useEffect(() => {
    setDriftAcknowledged(false);
  }, [geocodedGps?.lat, geocodedGps?.lon]);

  useEffect(() => {
    const q = debouncedAddress.trim();
    if (q.length < SUGGEST_MIN_CHARS) {
      setSuggestions([]);
      return;
    }
    let cancelled = false;
    // Coarsely rounded so nudging the pin does not invalidate the cache for
    // what is effectively the same neighbourhood.
    runSuggest({
      q,
      near: {
        lat: Number(pin.lat.toFixed(2)),
        lon: Number(pin.lon.toFixed(2)),
      },
    })
      .unwrap()
      .then((result) => {
        if (!cancelled) setSuggestions(result?.items ?? []);
      })
      // Suggestions are a convenience; a failure here is not worth surfacing.
      .catch(() => {
        if (!cancelled) setSuggestions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedAddress, runSuggest, pin.lat, pin.lon]);

  const driftMeters = geocodedGps ? haversineMeters(pin, geocodedGps) : null;
  const drifted = driftMeters !== null && driftMeters > DRIFT_THRESHOLD_M;

  return (
    <div className="flex flex-col gap-2 min-w-0">
      <Text type="secondary">Address</Text>
      <AutoComplete
        className="w-full"
        value={address}
        options={suggestions.map((value) => ({ value }))}
        onChange={(value: string) => onAddressChange(value ?? "")}
        // Picking a suggestion is the explicit choice that geocodes. The text
        // is passed through rather than read back from state, which has not
        // settled yet at this point.
        onSelect={(value: string) => {
          onAddressChange(value);
          onFind(value);
        }}
        getPopupContainer={() => container || document.body}
        // The panel sits at a higher z-index than antd's popup base, so
        // without this the suggestions open behind it and never appear.
        styles={{ popup: { root: { zIndex: POPUP_Z_INDEX } } }}
      >
        <Input
          placeholder="Start typing an address"
          // Enter geocodes what has been typed, for an address the suggester
          // does not offer.
          onPressEnter={() => onFind()}
          suffix={searching ? <LoadingOutlined /> : <span />}
        />
      </AutoComplete>

      <Button
        type="link"
        size="small"
        className="self-start p-0 h-auto"
        loading={reverseLoading}
        onClick={onUpdateFromPin}
      >
        Use the address at the pin
      </Button>

      {candidates.length > 1 && (
        <List
          size="small"
          bordered
          className="max-h-48 overflow-y-auto"
          dataSource={candidates}
          renderItem={(candidate) => (
            <List.Item
              className="cursor-pointer hover:bg-slate-50"
              onClick={() => onPickCandidate(candidate)}
            >
              <div className="flex flex-col min-w-0">
                <Text className="break-words">{candidate.address}</Text>
                <Text type="secondary" className="text-xs">
                  {formatDistance(haversineMeters(pin, candidate.gps))} from the
                  pin
                </Text>
              </div>
            </List.Item>
          )}
        />
      )}

      {lookupError && <Alert type="error" showIcon message={lookupError} />}

      {!lookupError && noMatch && (
        <Alert
          type="info"
          showIcon
          message="No match for that address."
          description="The pin stays where it is and the address is saved as you typed it."
        />
      )}

      {drifted && !driftAcknowledged && (
        <Alert
          type="warning"
          showIcon
          // The distance is the only legible signal at low zoom, where a
          // kilometre is less than a pixel and the drift line is invisible.
          message={`Pin is ${formatDistance(driftMeters!)} from this address.`}
          description={
            <Space wrap>
              <Button size="small" type="primary" onClick={onSnapPinToAddress}>
                Snap pin to address
              </Button>
              <Button size="small" onClick={() => setDriftAcknowledged(true)}>
                Keep pin where it is
              </Button>
            </Space>
          }
        />
      )}

      {drifted && driftAcknowledged && (
        <Text type="secondary" className="text-xs">
          Pin is {formatDistance(driftMeters!)} from the address.
        </Text>
      )}
    </div>
  );
};

export default AddressField;
